import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { reduce, isEqual, isEmpty } from "lodash";

import {
  getProjectDataSources,
  getProjectDataSourcesAreLoading,
  getProjectPage,
} from "../../../ducks/project/selectors";

import { formatDataSourceCondition } from "../utils";
import { setProjectDataSources, setProjectDataSourcesAreLoading } from "../../../ducks/project/project";
import { useDataSources } from "./useDataSources";
import { useWidgetConfiguration } from "./useWidgetConfiguration";
import { useDataSourceCondition } from "../../../hooks/layer/useDataSourceCondition";
import { useEwktGeometry } from "../../FeatureSelector/hooks/useEwktGeometry";
import { useIsConfiguration } from "../../../hooks/location/useIsConfiguration";

import { FilterConditionOperation } from "../../../components/LayerFilter/types";
import {
  ConfigDataSource,
  ConfigFilter,
  DataSourcePromise,
  SelectedFilters,
  WidgetType,
} from "../../FeatureCard/types";

// eslint-disable-next-line max-lines-per-function
export const useProjectDataSources = (filters: SelectedFilters) => {
  const dispatch = useDispatch();
  const projectDataSources = useSelector(getProjectDataSources);
  const isLoading = useSelector(getProjectDataSourcesAreLoading);
  const page = useSelector(getProjectPage);
  const ewktGeometry = useEwktGeometry();
  const getDashboardCondition = useDataSourceCondition();
  const isConfiguration = useIsConfiguration();
  const { currentConfig } = useWidgetConfiguration(WidgetType.Dashboard);

  const filtersDeps = useMemo(() => JSON.stringify(filters), [filters]);
  const prevFilters = useRef(filters);

  const dataSources: ConfigDataSource[] = useMemo(() => currentConfig?.dataSources ?? [], [currentConfig?.dataSources]);
  const configFilters: ConfigFilter[] = currentConfig?.filters;

  const { getDataSourcePromises, getUpdatingDataSources, getUpdatedDataSources, zoomToLayersExtent } = useDataSources({
    filters,
    config: currentConfig,
  });

  const isFilterChanged = useMemo(() => JSON.stringify(prevFilters.current) !== filtersDeps, [filtersDeps]);

  const fetchData = useCallback(
    (fetchingDataSources?: ConfigDataSource[]) => {
      const currentDataSources = (fetchingDataSources || dataSources)?.filter(({ condition, query, parameters }) => {
        const diffFilters = reduce(
          filters,
          (result, value, key) => {
            return isEqual(value, prevFilters.current[key]) ? result : result.concat(key);
          },
          [],
        );

        if (!diffFilters.length) return true;

        if (query) {
          if (isEmpty(parameters)) return false;

          return Object.keys(parameters).some(key =>
            diffFilters.some(filterName => parameters[key].includes(`%${filterName}`)),
          );
        }

        return formatDataSourceCondition({
          condition,
          configFilters,
          filters,
          geometry: ewktGeometry,
        });
      });

      if (!currentDataSources?.length) return;

      dispatch(setProjectDataSourcesAreLoading(true));

      const otherDataSources =
        projectDataSources?.length && currentDataSources?.length
          ? projectDataSources?.filter(({ name }) => !currentDataSources.some(item => item.name === name))
          : [];

      if (otherDataSources.length) {
        dispatch(setProjectDataSources(otherDataSources));
      }

      const notGeometryFilters = { ...filters, geometry: undefined };
      const prevNotGeometryFilters = { ...prevFilters.current, geometry: undefined };

      Promise.allSettled(currentDataSources.map(item => getDataSourcePromises(item)))
        .then((responses: DataSourcePromise[]) => {
          const responseDataSources = getUpdatedDataSources(responses, currentDataSources, otherDataSources);

          dispatch(setProjectDataSources(responseDataSources));

          if (JSON.stringify(notGeometryFilters) === JSON.stringify(prevNotGeometryFilters)) return;

          const zoomToLayers = currentConfig.layers?.filter(({ filterZoomTo }) => filterZoomTo);

          if (!zoomToLayers?.length) return;

          zoomToLayersExtent(
            zoomToLayers.map(({ name }) => ({
              layerName: name,
              query: getDashboardCondition(name).join(` ${FilterConditionOperation.AND} `),
            })),
          );
        })
        .finally(() => {
          dispatch(setProjectDataSourcesAreLoading(false));
        });
    },
    [
      dataSources,
      dispatch,
      projectDataSources,
      filters,
      getDataSourcePromises,
      configFilters,
      ewktGeometry,
      getUpdatedDataSources,
      currentConfig.layers,
      zoomToLayersExtent,
      getDashboardCondition,
    ],
  );

  const filterData = useCallback(() => {
    if (isLoading) return;

    if (!isFilterChanged) {
      dispatch(setProjectDataSources(null));
    }

    fetchData(isFilterChanged ? getUpdatingDataSources() : undefined);
  }, [dispatch, fetchData, getUpdatingDataSources, isFilterChanged, isLoading]);

  useEffect(() => {
    filterData();
  }, [page, filtersDeps]);

  useEffect(() => {
    if (isConfiguration) {
      filterData();
    }
  }, [currentConfig]);

  useEffect(() => {
    prevFilters.current = filters;
  }, [filters]);
};
